import { useState } from 'react';

const SuggestionItem = ({ suggestion, onSelect }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    const getConfidenceColor = (confidence) => {
        if (confidence >= 0.8) return 'bg-green-100 text-green-800';
        if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800';
        return 'bg-gray-100 text-gray-600';
    };

    const getMatchTypeIcon = (type) => {
        switch (type) {
            case 'context': return '🧠';
            case 'exact': return '🎯';
            case 'fuzzy': return '🔍';
            case 'keyword': return '🏷️';
            case 'popular': return '⭐';
            case 'ml_category': return '🤖';
            case 'ml_similarity': return '🔗';
            default: return '💬';
        }
    };

    const getMatchTypeLabel = (type) => {
        switch (type) {
            case 'context': return 'Context Match';
            case 'exact': return 'Exact Match';
            case 'fuzzy': return 'Fuzzy Match';
            case 'keyword': return 'Keyword Match';
            case 'popular': return 'Popular Response';
            case 'ml_category': return 'ML Category Match';
            case 'ml_similarity': return 'ML Similarity Match';
            default: return 'General Match';
        }
    };

    const formatLastUsed = (lastUsed) => {
        if (!lastUsed) return 'Never used';
        const date = new Date(lastUsed);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        if (diffDays > 0) return `${diffDays} days ago`;
        if (diffHours > 0) return `${diffHours} hours ago`;
        if (diffMinutes > 0) return `${diffMinutes} minutes ago`;
        return 'Just now';
    };

    const getSourceLabel = (source) => {
        switch (source) {
            case 'ml_medium_confidence': return 'ML Generated';
            case 'original_medium_confidence': return 'Historical Data';
            default: return source || 'Unknown';
        }
    };

    const getTooltipContent = () => {
        const { ml_metadata } = suggestion;

        return (
            <div className="bg-gray-900 text-white p-3 rounded-lg shadow-lg max-w-xs text-xs leading-relaxed">
                <div className="font-semibold mb-2 text-gray-100">Response Details</div>

                <div className="space-y-1.5">
                    <div className="flex justify-between">
                        <span className="text-white font-medium whitespace-pre-wrap overflow-x-hidden">{suggestion.response}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-300">Match Type:</span>
                        <span className="text-white font-medium">{getMatchTypeLabel(suggestion.match_type)}</span>
                    </div>

                    <div className="flex justify-between">
                        <span className="text-gray-300">Confidence:</span>
                        <span className="text-white font-medium">{Math.round(suggestion.confidence * 100)}%</span>
                    </div>

                    <div className="flex justify-between">
                        <span className="text-gray-300">Usage Count:</span>
                        <span className="text-white font-medium">{suggestion.frequency}x</span>
                    </div>

                    <div className="flex justify-between">
                        <span className="text-gray-300">Last Used:</span>
                        <span className="text-white font-medium">{formatLastUsed(suggestion.last_used)}</span>
                    </div>

                    <div className="flex justify-between">
                        <span className="text-gray-300">Source:</span>
                        <span className="text-white font-medium">{getSourceLabel(suggestion.source)}</span>
                    </div>

                    {ml_metadata && (
                        <>
                            <div className="border-t border-gray-700 pt-1.5 mt-2">
                                <div className="text-gray-300 font-medium mb-1">ML Metadata:</div>

                                {ml_metadata.category && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Category:</span>
                                        <span className="text-white capitalize">{ml_metadata.category}</span>
                                    </div>
                                )}

                                {ml_metadata.sentiment && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Sentiment:</span>
                                        <span className="text-white capitalize">{ml_metadata.sentiment}</span>
                                    </div>
                                )}

                                {ml_metadata.similarity_score && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Similarity:</span>
                                        <span className="text-white">{Math.round(ml_metadata.similarity_score * 100)}%</span>
                                    </div>
                                )}

                                {ml_metadata.context_score && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Context:</span>
                                        <span className="text-white">{Math.round(ml_metadata.context_score * 100)}%</span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {suggestion.original_customer_message && (
                        <div className="border-t border-gray-700 pt-1.5 mt-2">
                            <div className="text-gray-300 font-medium mb-1">Original Context:</div>
                            <div className="text-gray-200 italic text-xs">
                                "{suggestion.original_customer_message}"
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="relative">
            <div
                className="p-2 px-4 border border-gray-200 bg-white rounded-full cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={onSelect}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                <div className="flex items-start justify-between">
                    <p className="text-sm text-gray-800 flex-1 mr-2 max-w-[150px] truncate">
                        {suggestion.response}
                    </p>
                    <div className="flex items-center space-x-1">
                        <span className="text-xs" title={`Match type: ${suggestion.match_type}`}>
                            {getMatchTypeIcon(suggestion.match_type)}
                        </span>
                        <span
                            className={`px-1.5 py-0.5 rounded text-xs font-medium ${getConfidenceColor(suggestion.confidence)}`}
                            title={`Confidence: ${Math.round(suggestion.confidence * 100)}%`}
                        >
                            {Math.round(suggestion.confidence * 100)}%
                        </span>
                    </div>
                </div>
                {/*suggestion.frequency > 1 && (
                    <p className="text-xs text-gray-500 mt-1">
                        Used {suggestion.frequency} times
                    </p>
                )*/}
            </div>

            {/* Tooltip */}
            {showTooltip && (
                <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 pointer-events-none min-w-[200px]">
                    {getTooltipContent()}
                    {/* Tooltip arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
            )}
        </div>
    );
};

export default SuggestionItem;